import { useState } from 'react';
import { useDynamicDrawerContext } from '../../../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../../../context/snackbar.context';
import {
  BillStatusEnum,
  GetBillsDocument,
  useBulkUpdateBillsMutation,
} from '../../../../types';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../../grid-components/grid-types';
import { PanelAlert } from '../../../grid-components/panel-alert';
import { PanelLayout } from '../../../grid-components/panel-layout';
import { PanelTitle } from '../../../grid-components/panel-title';
import { noteInvalid } from '../../../grid-components/panel-validation';
import { BillTypeDataList } from '../../bill-grid-components/bill-management-types';
import { BillPanelProps } from '../panel-types';
import { SelectedBillsGrid } from './selected-grid';

export const ArchivePanel = ({
  selectedRows,
  clients,
  insurers,
  onClearSelections,
}: BillPanelProps): JSX.Element => {
  const { setIsOpen, setTitle } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const [panelSelectedRows, setPanelSelectedRows] =
    useState<BillTypeDataList>(selectedRows);
  const [note, setNote] = useState<string>('');

  const [bulkUpdateBills, { loading }] = useBulkUpdateBillsMutation();

  const handleArchive = async (): Promise<void> => {
    try {
      const result = await bulkUpdateBills({
        variables: {
          bulkUpdateBillsInput: {
            billIds: panelSelectedRows.map(row => row.billId),
            statusChange: BillStatusEnum.Archived,
            note,
          },
        },
        refetchQueries: [GetBillsDocument],
      });
      setSnackbarState(
        result.data?.bulkUpdateBills?.details ?? 'Successfully archived bills',
        'success',
        5000,
      );
      onClearSelections();
      setTimeout(() => setIsOpen(false), 500);
    } catch (e) {
      setSnackbarState(
        `Failed to archive bills with error: ${e instanceof Error ? e.message : 'Unknown error'}`,
        'error',
        5000,
      );
    }
  };

  const handleSelectionChanged = (selectedGridRows: BillTypeDataList): void => {
    setPanelSelectedRows(selectedGridRows);
    setTitle(
      <PanelTitle
        actionType={ActionTypeDisplayText.ARCHIVE}
        rowCount={selectedGridRows.length}
        gridLevel={GridLevelName.BILL}
      />,
    );
  };

  return (
    <PanelLayout
      selectedGrid={
        <SelectedBillsGrid
          selectedRows={panelSelectedRows}
          setPanelSelectedRows={handleSelectionChanged}
          clients={clients}
          insurers={insurers}
        />
      }
      alert={<PanelAlert drawerMode="archive" />}
      note={note}
      setNote={setNote}
      setIsOpen={setIsOpen}
      isSubmitting={loading}
      isInvalid={noteInvalid(note)}
      actionLabel="Archive"
      handleSubmit={handleArchive}
    />
  );
};
